import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import useLiveChannel from '../../hooks/useLiveChannel';
import useChatPush from '../../hooks/useChatPush';
import { SAFETY_NET_MS } from '../../utils/live/wsTransport';
import { latestId, mergeMessages, stillUnsent } from '../../utils/chat/messages';
import { newClientKey, retryable, sendEntry } from '../../utils/chat/outbox';
import MessageItem from './MessageItem';
import MembersDialog from './MembersDialog';
import Composer from './Composer';
import * as S from '../specimens/styles';

/*
 * One open conversation (routes/chat.py): its newest messages, kept current by
 * a live channel (asked every few seconds while the page is shown; the newest
 * page again each time, so a reaction or an edit shows too, and the messages
 * after the newest shown when more than a page came in between), older ones
 * on request, and a composer.
 *
 * WHAT IS WRITTEN IS NEVER LOST. A message goes into the device's chat outbox
 * (utils/chat/outbox.js) under a client_key before it is sent, and leaves it
 * when the server has it. No answer, the server busy, or mid-deploy: it stays
 * "sending" and is tried again with the next poll. Refused for good (too
 * long, not a member any more): it stays as "not sent", to retry or discard.
 * The server keeps a message by its client_key, so a retry after a lost
 * answer never makes a second one. A message's photographs and voice notes
 * wait in the outbox entry with it, go up first, and leave with it
 * (utils/chat/outbox.js sendEntry).
 *
 * What others wrote is marked read (PUT .../read) once it is on screen with
 * the page shown; the header's unread badge hears of it at once.
 */
export const READ_EVENT = 'pitriot:chat-read';
const NOT_FOUND = Symbol('not found');

export default function Conversation({ channelId, me, outbox, onBack, onLeft, onChanged }) {
  const { t } = useTranslation(['chat', 'common']);
  const [info, setInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [moreBefore, setMoreBefore] = useState(false);
  const [pending, setPending] = useState([]);
  const [error, setError] = useState('');
  const [membersOpen, setMembersOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const shown = useRef([]);
  shown.current = messages;
  const unsent = useRef([]);
  unsent.current = pending;
  const readSent = useRef(0);
  const sending = useRef(new Set());
  const delivered = useRef(new Set());
  // Refused for good: not tried again until "Try again" (kept apart from
  // state, which a poll could read a render late).
  const refused = useRef(new Set());
  const listEnd = useRef(null);

  // No `t` here: an effect keyed on it would re-run whenever its identity changes.
  const loadInfo = useCallback(() => api.get(`/chat/channels/${channelId}`)
    .then((r) => setInfo(r.data))
    .catch((e) => setError(e?.response?.status === 404 ? NOT_FOUND : errorText(e))), [channelId]);

  useEffect(() => {
    setInfo(null); setMessages([]); setLoaded(false); setError(''); readSent.current = 0;
    loadInfo();
    outbox.list(channelId).then(setPending);
  }, [channelId, loadInfo, outbox]);

  const deliver = useCallback((entry) => {
    const key = entry.client_key;
    // Sent already: a poll that began before the answer came must not send it
    // again (which would put its files up twice).
    if (sending.current.has(key) || delivered.current.has(key)) return Promise.resolve();
    sending.current.add(key);
    let latest = entry;
    // What went up is kept, on screen and in the outbox: a retry sends only what is missing.
    const save = (progress) => {
      latest = progress;
      setPending((p) => p.map((x) => (x.client_key === key ? progress : x)));
      return outbox.update(progress);
    };
    return sendEntry(entry, { client: api, save })
      .then((sent) => {
        if (!sent) return undefined;                     // no message in the answer: still pending, tried again
        delivered.current.add(key);
        setMessages((m) => mergeMessages(m, [sent]));
        setPending((p) => p.filter((e) => e.client_key !== entry.client_key));
        return outbox.remove(entry.client_key);
      })
      .catch((e) => {
        if (retryable(e)) return undefined;            // still pending: the next poll tries again
        refused.current.add(entry.client_key);
        const failed = { ...latest, state: 'failed', error: errorText(e) };
        setPending((p) => p.map((x) => (x.client_key === entry.client_key ? failed : x)));
        return outbox.update(failed);
      })
      .finally(() => sending.current.delete(entry.client_key));
  }, [outbox]);

  const pull = useCallback(async () => {
    const page = (await api.get(`/chat/channels/${channelId}/messages`, { params: { limit: 50 } })).data;
    const had = latestId(shown.current);
    let incoming = page.messages || [];
    // More than a page arrived since the last look: fetch what lies between.
    if (had && page.more_before && incoming.length && incoming[0].message_id > had + 1) {
      const gap = (await api.get(`/chat/channels/${channelId}/messages`, { params: { after: had, limit: 200 } })).data;
      incoming = [...(gap.messages || []), ...incoming];
    }
    return { page, incoming, first: !had };
  }, [channelId]);

  const onData = useCallback(({ page, incoming, first }) => {
    setMessages((m) => mergeMessages(m, incoming));
    if (first) setMoreBefore(!!page.more_before);
    setLoaded(true);
    // What the server now has leaves the outbox; the rest is tried again.
    const left = stillUnsent(unsent.current, incoming);
    unsent.current.filter((e) => !left.includes(e)).forEach((e) => { outbox.remove(e.client_key); });
    setPending((p) => { const still = stillUnsent(p, incoming); return still.length === p.length ? p : still; });
    left.filter((e) => e.state !== 'failed' && !refused.current.has(e.client_key)).forEach((e) => { deliver(e); });
  }, [deliver, outbox]);

  // Pushed while the chat's socket is open (phase D2): a poke for this
  // conversation reads now, and polling slows to the safety net.
  const pokeRef = useRef(null);
  const live = useChatPush({ channelId, onPoke: () => pokeRef.current?.() });
  const poke = useLiveChannel(pull, onData, live
    ? { base: SAFETY_NET_MS, max: SAFETY_NET_MS, key: channelId } : { base: 4000, key: channelId });
  pokeRef.current = poke;

  // Read up to the newest message someone else wrote, once it is on screen.
  useEffect(() => {
    const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
    const theirs = messages.filter((m) => m.sender_id !== me).reduce((top, m) => Math.max(top, m.message_id), 0);
    if (!visible || !theirs || theirs <= readSent.current) return;
    readSent.current = theirs;
    api.put(`/chat/channels/${channelId}/read`, { message_id: theirs })
      .then(() => { window.dispatchEvent(new Event(READ_EVENT)); onChanged?.(); })
      .catch(() => { readSent.current = 0; });
  }, [messages, me, channelId, onChanged]);

  useEffect(() => {
    listEnd.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length, pending.length]);

  const send = async ({ body, attachments = [] }) => {
    if (!body && !attachments.length) return;
    const entry = { client_key: newClientKey(), channel_id: channelId, body, created_at: new Date().toISOString(),
      state: 'pending', ...(attachments.length ? { attachments } : {}) };
    setPending((p) => [...p, entry]);
    await outbox.add(entry);
    await deliver(entry);
    poke();
  };

  const olderPage = () => {
    const oldest = messages[0]?.message_id;
    if (!oldest) return;
    api.get(`/chat/channels/${channelId}/messages`, { params: { before: oldest, limit: 50 } })
      .then((r) => { setMessages((m) => mergeMessages(m, r.data.messages)); setMoreBefore(!!r.data.more_before); })
      .catch((e) => setError(errorText(e)));
  };

  const setMembership = (patch) => api.put(`/chat/channels/${channelId}/membership`, patch)
    .then((r) => { setInfo((i) => ({ ...i, ...r.data })); onChanged?.(); window.dispatchEvent(new Event(READ_EVENT)); })
    .catch((e) => setError(errorText(e)));

  const message = error === NOT_FOUND ? t('notFound') : error;
  if (error && !info) {
    return <div style={{ padding: 16 }}><p role="alert" style={S.error}>{message}</p></div>;
  }
  if (!info) return <div style={{ padding: 16, ...S.hint }}>{t('loading')}</div>;

  const canSend = !!info.can?.send;
  const muteValue = info.muted ? 'on' : 'off';
  return (
    <section aria-label={info.title || t('conversation')} data-testid="chat-conversation"
      style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', flexWrap: 'wrap',
        borderBottom: '1px solid var(--c-border)' }}>
        {onBack && (
          <button type="button" style={S.ghostBtn} onClick={onBack} data-testid="chat-back" aria-label={t('back')}>
            {t('back')}
          </button>
        )}
        <h2 style={{ margin: 0, fontSize: 17, flex: 1, minWidth: 120 }} dir="auto">{info.title || t('untitled')}</h2>
        <button type="button" style={S.ghostBtn} onClick={() => setMembersOpen(true)} data-testid="chat-members-open"
          title={t('tip.members')}>{t('memberCount', { count: info.members?.length || 0 })}</button>
        <label style={inline}>
          <span>{t('muteLabel')}</span>
          <select value={muteValue} style={S.input} data-testid="chat-mute"
            onChange={(e) => setMembership({ mute: e.target.value === 'on' ? 'forever' : e.target.value })}>
            <option value="off">{t('mute.off')}</option>
            <option value="8h">{t('mute.8h')}</option>
            <option value="24h">{t('mute.24h')}</option>
            <option value="on">{info.muted ? t('mute.on') : t('mute.forever')}</option>
          </select>
        </label>
        <label style={inline}>
          <span>{t('notifyLabel')}</span>
          <select value={info.notify} style={S.input} data-testid="chat-notify"
            onChange={(e) => setMembership({ notify: e.target.value })}>
            {['all', 'mentions', 'none'].map((n) => <option key={n} value={n}>{t(`notify.${n}`)}</option>)}
          </select>
        </label>
      </header>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', minHeight: 200 }} aria-live="polite"
        aria-relevant="additions" data-testid="chat-scroll">
        {moreBefore && (
          <div style={{ textAlign: 'center' }}>
            <button type="button" style={S.ghostBtn} onClick={olderPage} data-testid="chat-older">{t('older')}</button>
          </div>
        )}
        {loaded && messages.length === 0 && pending.length === 0 && <p style={S.hint}>{t('empty')}</p>}
        <ol style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="chat-messages">
          {messages.map((m) => (
            <MessageItem key={m.message_id} message={m} me={me} canSend={canSend}
              onChanged={(next) => setMessages((list) => mergeMessages(list, [next]))} />
          ))}
          {pending.map((e) => (
            <li key={e.client_key} data-testid="chat-pending" data-state={e.state}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', margin: '6px 0' }}>
              <div style={{ ...S.hint, marginBottom: 2 }}>{e.state === 'failed' ? t('notSent') : t('sending')}</div>
              <div style={{ border: '1px dashed var(--c-border-strong)', borderRadius: 12, padding: '8px 12px',
                maxWidth: 'min(560px, 85%)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', opacity: 0.8 }} dir="auto">
                {e.attachments?.length > 0 && (
                  <div style={S.hint} data-testid="chat-pending-attachments">{carried(e.attachments, t)}</div>
                )}
                {e.body}
              </div>
              {e.state === 'failed' && (
                <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
                  <span style={S.error}>{e.error}</span>
                  <button type="button" style={small} data-testid="chat-retry"
                    onClick={() => { refused.current.delete(e.client_key);
                      const again = { ...e, state: 'pending', error: undefined };
                      setPending((p) => p.map((x) => (x.client_key === e.client_key ? again : x)));
                      outbox.update(again).then(() => deliver(again)); }}>{t('retry')}</button>
                  <button type="button" style={small} data-testid="chat-discard"
                    onClick={() => { setPending((p) => p.filter((x) => x.client_key !== e.client_key));
                      outbox.remove(e.client_key); }}>{t('discard')}</button>
                </div>
              )}
            </li>
          ))}
        </ol>
        <div ref={listEnd} />
      </div>

      {error && <p role="alert" style={{ ...S.error, padding: '0 12px' }}>{message}</p>}
      <Composer canSend={canSend} allowed={info.can?.attach || []} me={me} onSend={send} />

      {membersOpen && (
        <MembersDialog channel={info} me={me} onClose={() => setMembersOpen(false)}
          onChanged={() => { loadInfo(); poke(); onChanged?.(); }}
          onLeft={() => { setMembersOpen(false); onLeft?.(); }} />
      )}
    </section>
  );
}

/** "2 photos · 1 voice note · 1 place": what an unsent message carries. */
function carried(attachments, t) {
  const counts = {};
  attachments.forEach((a) => { counts[a.kind] = (counts[a.kind] || 0) + 1; });
  return Object.entries(counts).map(([kind, count]) => t(`attach.count.${kind}`, { count })).join(' · ');
}

const inline = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 };
const small = { ...S.ghostBtn, padding: '2px 8px', fontSize: 12 };
